import React, { useCallback, useContext, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import TourV2TrendingDestinationsQuickFilters from 'components/Search/TourV2/TourV2TrendingDestinationsQuickFilters'
import styled from 'styled-components'
import { rem } from 'polished'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { TOUR_V2_LOCATION_SEARCH_TYPEAHEAD_TYPES, TOUR_V2_SEARCH_TYPES, tourDurationOptions } from 'constants/tours'
import LocationFloatingPanel from '../Panels/LocationFloatingPanel'
import SearchFormField from '../../SearchFormField/SearchFormField'
import { prettyFlexibleDates } from 'components/Search/SearchForm/SearchDateInput/FlexibleDateSearchField'
import DateFloatingPanel from 'components/SearchV2/Components/Mobile/Panels/DateFloatingPanel'
import useStepsController from 'components/SearchV2/Hooks/useStepsController'
import useDateSearchProps from 'components/SearchV2/Hooks/useDateSearchProps'
import TourGroupTypePanel from 'components/SearchV2/Components/Mobile/Panels/TourGroupTypePanel'
import { useGlobalToursSearchContext } from '../../../../../hooks/GlobalSearch/GlobalSearchVerticals/useGlobalToursSearch'

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const FieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;

  > * {
    &:not(:first-child) {
      margin-top: ${rem(8)};
    }
  }
`

const TrendingDestinations = styled.div`
  margin-top: ${rem(24)};
`

enum SearchSteps {
  LOCATION = 'location',
  DATE = 'date',
  GROUP_TYPE = 'group_type',
}

interface Props {
  closeSearch: () => void
  onSearch: () => void
  isInPage?: boolean
}

export default function ToursTabContent(props: Props) {
  const { closeSearch, onSearch, isInPage } = props
  const intl = useIntl()

  const {
    searchItem,
    flexibleMonths,
    flexibleNights,
    userSelectedFlexibleMonths,
    tourGroupTypes = [],
  } = useContext(GlobalSearchStateContext)

  const [activeStep, setActiveStep] = useState<SearchSteps>()

  const stepsValidation = useMemo(() => ({
    [SearchSteps.LOCATION]: searchItem,
    [SearchSteps.DATE]: flexibleNights,
    [SearchSteps.GROUP_TYPE]: tourGroupTypes,
  }), [searchItem, flexibleNights, tourGroupTypes])

  const {
    goToNextStep,
    goToPreviousStep,
    closeActiveStep,
    isSearchEnabled,
  } = useStepsController({ activeStep, setActiveStep, stepsValidation, onSearch })

  const dateSearchProps = useDateSearchProps({ onAnytimeDatesSelected: onSearch, isSpecificSearchEnabled: false })

  const { globalToursSearchState } = useGlobalToursSearchContext()

  const toursSearchLabel = intl.formatMessage({
    id: '4Hbg2j7fKg',
    defaultMessage: 'Search',
    description: 'Search button text for tours date panel',
  })
  const toursApplyLabel = intl.formatMessage({
    id: '9YYrkIRLjP',
    defaultMessage: 'Apply',
    description: 'Apply button text for tours date panel',
  })

  const flexibleSearchDateSelectedText =
    prettyFlexibleDates(userSelectedFlexibleMonths ? flexibleMonths : undefined, flexibleNights, tourDurationOptions)

  const openLocationSearchStep = useCallback(() => setActiveStep(SearchSteps.LOCATION), [setActiveStep])
  const openDateSearchStep = useCallback(() => setActiveStep(SearchSteps.DATE), [setActiveStep])

  return (
    <ContentWrapper>
      <LocationFloatingPanel
        isOpen={activeStep === SearchSteps.LOCATION}
        onClose={closeActiveStep}
        onGoBack={goToPreviousStep}
        onConfirm={goToNextStep}
        searchTypes={TOUR_V2_SEARCH_TYPES}
        typeaheadTypes={TOUR_V2_LOCATION_SEARCH_TYPEAHEAD_TYPES}
        typeaheadVertical="tour"
        popularPlaces={globalToursSearchState.popularDestinations}
      />

      <DateFloatingPanel
        {...dateSearchProps}
        isOpen={activeStep === SearchSteps.DATE}
        onClose={closeActiveStep}
        onConfirm={goToNextStep}
        onGoBack={goToPreviousStep}
        confirmText={isSearchEnabled ? toursSearchLabel : toursApplyLabel}
        durationOptionsCustom={tourDurationOptions}
        hasDates={false}
        hideDatePicker
        showFlexibleYearSelector
      />

      <TourGroupTypePanel
        isOpen={activeStep === SearchSteps.GROUP_TYPE}
        onClose={closeActiveStep}
        onGoBack={goToPreviousStep}
        onConfirm={goToNextStep}
      />

      <FieldsWrapper>
        <SearchFormField
          label={intl.formatMessage({
            id: 'a2dDg2n9uR',
            defaultMessage: 'Where',
            description: 'Label for tour destination search field',
          })}
          placeholder={intl.formatMessage({
            id: '8l/4RIPG/y',
            defaultMessage: 'Search destination or place',
            description: 'Placeholder for tour destination search field',
          })}
          value={searchItem?.format?.mainText ?? ''}
          onClick={openLocationSearchStep}
        />

        <SearchFormField
          label={intl.formatMessage({
            id: 'QPbHRqIWDV',
            defaultMessage: 'When',
            description: 'Label for tour date search field',
          })}
          onClick={openDateSearchStep}
          placeholder={intl.formatMessage({
            id: '5y7zxUXA1/',
            defaultMessage: 'Anytime',
            description: 'Placeholder for tour date search field when no date selected',
          })}
          value={flexibleSearchDateSelectedText}
        />

      </FieldsWrapper>
      {!isInPage && <TrendingDestinations>
        <TourV2TrendingDestinationsQuickFilters
          titleColour="neutral-two"
          titleWeight="normal"
          titleLabel={intl.formatMessage({
            id: 'zorSARxDBN',
            defaultMessage: 'Popular searches',
            description: 'Title label for tour trending destinations section',
          })}
          onDestinationClick={closeSearch}
        />
      </TrendingDestinations>}
    </ContentWrapper>
  )
}
